import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { itSupportPlans } from "../mock";

const fmtEur = (n) =>
  n % 1 === 0
    ? `€${n}`
    : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n);

const ITSupportSection = () => {
  return (
    <section className="relative py-20 overflow-hidden bg-white" data-testid="its-section">
      {/* Subtle gold accents */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 right-0 w-[560px] h-[560px] bg-amber-100/40 blur-3xl -translate-y-1/4 translate-x-1/4 rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-50 blur-3xl translate-y-1/3 -translate-x-1/4 rounded-full"></div>
      </div>

      <div className="max-w-[1280px] mx-auto px-5 md:px-10 relative">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 leading-[1.05] tracking-tight mb-5">
            Altijd zorgeloos <span className="italic font-light">werken.</span>
          </h2>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Met het Computheer IT Support-abonnement heeft u nooit meer stress om virussen,
            updates of een trage computer. Wij houden alles voor u in de gaten — u werkt gewoon door.
          </p>
        </div>

        {/* Two dark cards side by side */}
        <div className="grid md:grid-cols-2 gap-6 max-w-[980px] mx-auto">
          {itSupportPlans.map((p) => (
            <div
              key={p.id}
              data-testid={`its-home-card-${p.id}`}
              className={`relative flex flex-col bg-neutral-900 text-white p-8 md:p-10 shadow-2xl border ${
                p.business ? "border-amber-500" : "border-neutral-800"
              }`}
            >
              {p.business && (
                <div className="absolute -top-3 right-6 bg-amber-500 text-neutral-900 text-[10px] font-bold tracking-widest uppercase px-3 py-1">
                  Voor bedrijven
                </div>
              )}
              <div className="mb-6">
                <p className="text-[11px] uppercase tracking-widest text-neutral-400 mb-1">{p.tagline}</p>
                <h3 className="text-xl md:text-2xl font-bold text-white">{p.name}</h3>
              </div>

              <div className="mb-7">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl md:text-6xl font-bold text-white">{fmtEur(p.priceMonthly)}</span>
                  <span className="text-sm text-neutral-400">/ maand</span>
                </div>
                <p className="text-sm text-neutral-400 mt-1.5">
                  of <span className="text-neutral-200 font-medium">{fmtEur(p.priceYearly)} / jaar</span>
                  {p.yearlyNote && <span> — <span className={p.business ? "text-neutral-400" : "text-amber-300"}>{p.yearlyNote}</span></span>}
                </p>
                {p.deviceLimit && (
                  <p className="text-xs text-neutral-500 mt-2 italic">{p.deviceLimit}</p>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px] text-neutral-100">
                    <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${p.business ? "text-amber-400" : "text-emerald-400"}`}/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={p.business ? "/zakelijk" : "/it-support"}
                className={`group inline-flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-colors ${
                  p.business
                    ? "bg-amber-500 hover:bg-amber-400 text-neutral-900"
                    : "bg-white hover:bg-neutral-100 text-neutral-900"
                }`}
                data-testid={`its-home-cta-${p.id}`}
              >
                {p.cta}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ITSupportSection;
